import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '../lib/cn'
import { formatCompactNumber, formatPercent } from '../lib/format'
import type { Tone } from '../lib/labels'
import { IconChevronRight } from './icons'
import { InfoHint } from './InfoHint'
import { Skeleton } from './Spinner'

const DELTA_TONE: Record<'up' | 'down' | 'flat', string> = {
  up: 'text-success-text',
  down: 'text-danger-text',
  flat: 'text-ink-subtle',
}

const VALUE_TONE: Record<Tone, string> = {
  neutral: 'text-ink',
  info: 'text-info-text',
  success: 'text-success-text',
  warning: 'text-warning-text',
  danger: 'text-danger-text',
  accent: 'text-accent-text',
}

export interface StatProps {
  label: ReactNode
  /** A number is formatted for you. Pass a node when you need custom units. */
  value: ReactNode | number
  /** One short line of context under the number. */
  hint?: ReactNode
  /** Signed change against the previous period, as a fraction. */
  delta?: number | null
  deltaLabel?: string
  /** Bigger is better by default. Set false for things like bounce rate. */
  higherIsBetter?: boolean
  icon?: ReactNode
  tone?: Tone
  /** Makes the whole tile a link into the detail behind the number. */
  to?: string
  isLoading?: boolean
  className?: string
  /**
   * The small grey chip after the value, holding the absolute and the share:
   * `41 (12%)`.
   */
  chip?: ReactNode
  /** What the number means, behind a help marker after the label. */
  help?: ReactNode
}

/**
 * The small grey chip a rate or a share sits in, beside the number it belongs
 * to: `41 (12%)` on a stat tile, `12.5%` in a table cell. 20 tall, 12/18 500
 * in the secondary ink on the selected grey.
 */
export function PercentChip({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      data-numeric=""
      className={cn(
        'inline-flex h-5 shrink-0 items-center rounded-md bg-surface-selected px-1.5 text-xs font-medium tabular-nums text-ink-secondary',
        className,
      )}
    >
      {children}
    </span>
  )
}

/**
 * A single KPI, as measured: about 237 by 84 (the 220 floor is applied by a
 * scrolling `StatGrid`, because in a five-column grid on a laptop it would
 * push the row off the page), the label row with a 16px icon
 * and an optional help marker, then the value in 20/30 with its chip beside it.
 */
export function Stat({
  label,
  value,
  hint,
  delta,
  deltaLabel,
  higherIsBetter = true,
  icon,
  tone = 'neutral',
  to,
  isLoading,
  className,
  chip,
  help,
}: StatProps) {
  const direction: 'up' | 'down' | 'flat' =
    delta === null || delta === undefined || Math.abs(delta) < 0.0005 ? 'flat' : delta > 0 ? 'up' : 'down'
  const good = direction === 'flat' ? 'flat' : (direction === 'up') === higherIsBetter ? 'up' : 'down'

  const body = (
    <>
      <div className="flex items-center gap-2">
        {icon ? <span className="inline-flex shrink-0 text-ink-subtle [&>svg]:h-4 [&>svg]:w-4">{icon}</span> : null}
        <p className="min-w-0 truncate text-base font-medium text-ink-muted">
          {label}
          {help ? <InfoHint label={typeof label === 'string' ? label : 'this number'}>{help}</InfoHint> : null}
        </p>
        {/*
          A TILE THAT GOES SOMEWHERE HAS TO LOOK LIKE IT DOES. These have been
          links since they were written, and the only cue was a hover shadow, so
          on a touchscreen there was no cue at all and on a mouse you had to
          already suspect. A chevron is the cheapest possible statement that
          there is something behind the number, and it is the one every console
          in this class uses. The caller's own icon still wins the slot when
          there is one, because a tile that says what it is beats a tile that
          says it is clickable.
        */}
        {to ? (
          <IconChevronRight
            aria-hidden
            className="ml-auto h-4 w-4 shrink-0 text-ink-faint transition group-hover:translate-x-0.5 group-hover:text-ink-subtle"
          />
        ) : null}
      </div>
      {isLoading ? (
        <Skeleton className="mt-2 h-[30px] w-20" />
      ) : (
        <div className="mt-2 flex items-center gap-2">
          <p data-numeric="" className={cn('text-2xl font-semibold tabular-nums', VALUE_TONE[tone])}>
            {typeof value === 'number' ? formatCompactNumber(value) : value}
          </p>
          {chip !== undefined && chip !== null ? <PercentChip>{chip}</PercentChip> : null}
        </div>
      )}
      {(delta !== null && delta !== undefined && !isLoading) || hint ? (
      <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        {delta !== null && delta !== undefined && !isLoading ? (
          <span className={cn('text-xs font-medium tabular-nums', DELTA_TONE[good])}>
            {direction === 'up' ? '+' : ''}
            {formatPercent(delta, { digits: 1 })}
            {deltaLabel ? <span className="ml-1 font-normal text-ink-subtle">{deltaLabel}</span> : null}
          </span>
        ) : null}
        {hint ? <span className="text-xs text-ink-muted">{hint}</span> : null}
      </div>
      ) : null}
    </>
  )

  const shell = cn(
    'block min-h-[84px] min-w-0 rounded-xl border border-line bg-surface p-4 shadow-xs transition',
    // `group` so the chevron can react to a hover on the whole tile, and a focus
    // ring because a link with no visible focus is a link a keyboard cannot see.
    // Both are on the link only: a plain readout is not in the tab order and
    // should not pretend to be.
    to &&
      'group hover:border-line-strong focus-visible:outline-none focus-visible:shadow-focus',
    className,
  )

  return to ? (
    <Link to={to} className={shell}>
      {body}
    </Link>
  ) : (
    <div className={shell}>{body}</div>
  )
}

/**
 * A responsive row of stat tiles.
 *
 * `scroll` keeps them in one row that scrolls sideways and snaps to a tile's
 * start, for the channel block, where wrapping would break the row the
 * carousel animates.
 */
export function StatGrid({
  children,
  columns = 4,
  className,
  scroll = false,
}: {
  children: ReactNode
  columns?: 2 | 3 | 4 | 5
  className?: string
  scroll?: boolean
}) {
  if (scroll) {
    return (
      <div className={cn('flex snap-x snap-mandatory gap-3 overflow-x-auto overflow-y-hidden [&>*]:min-w-[220px] [&>*]:shrink-0 [&>*]:snap-start', className)}>
        {children}
      </div>
    )
  }
  const map: Record<number, string> = {
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-2 lg:grid-cols-3',
    4: 'sm:grid-cols-2 lg:grid-cols-4',
    5: 'sm:grid-cols-2 lg:grid-cols-5',
  }
  return <div className={cn('grid grid-cols-1 gap-3', map[columns], className)}>{children}</div>
}
